import numbro from 'numbro';
import { ChangeEvent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  JOYAJOYPrice,
  WEATeacherPlanPrice,
} from '../../../../constants/exchangeRates';
import {
  getTextToGameVideoPreview,
  getWEAProgramImage,
} from '../../../../constants/images';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import { getLocalCurrencyById } from '../../../../utils/region';
import {
  ChangeCountry,
  LanguageRow,
  LoadingSpinner,
  TextButton,
  TextButtonFontSize,
  TextButtonSize,
  TextButtonVariant,
  VideoPlayer,
} from '../../../Molecules';

import { getCreateGameVideo } from '../../../../constants/videos';
import './WEATeacherMembershipProgramDetailsTemplate.css';

type TextInputProps = {
  value: string;
  disabled: boolean;
  errorMessage: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
};

export type WEATeacherMembershipProgramDetailsTemplateProps = {
  originalYearCurrency: string;
  originalMonthCurrency: string;
  referralYearCurrency: string | undefined;
  referralMonthCurrency: string | undefined;
  referralCode: TextInputProps;
  validationLoading: boolean;
  purchasedPlan: boolean;
  onClickReferral: () => void;
  onClickPurchase: () => void;
  onClickWEA: () => void;
  onClickJoyajoy: () => void;
  onClickHere: () => void;
  isReferred: boolean;
};

export const WEATeacherMembershipProgramDetailsTemplate = memo(
  ({
    originalYearCurrency,
    originalMonthCurrency,
    referralYearCurrency,
    referralMonthCurrency,
    validationLoading,
    purchasedPlan,
    onClickReferral,
    onClickPurchase,
    onClickWEA,
    onClickJoyajoy,
    onClickHere,
    isReferred,
  }: WEATeacherMembershipProgramDetailsTemplateProps) => {
    const { t, i18n } = useTranslation();
    const activeUserId = useSessionStore((store) => store.activeUserId);
    const userProfile = useUserStore((state) =>
      state.getProfile(activeUserId ?? ''),
    );
    const activeRegion = useSessionStore((store) => store.activeRegion);
    const currency = useMemo(() => {
      return userProfile?.localCurrency ?? getLocalCurrencyById(activeRegion);
    }, [activeRegion, userProfile?.localCurrency]);
    return (
      <div className="wea-teacher-program">
        {purchasedPlan && (
          <div className="purchased-container">
            <div className="container-weablue1">
              <p className="text-white-l">
                {t('You are now a WEA Teacher Member!')}
              </p>
            </div>
            <div className="container-white">
              <p className="text-black-s">
                {t(
                  'Some of your membership benefits will be available after 5-10 working days:',
                )}
              </p>
              <ul className="wea-list">
                <li className="wea-listItem">
                  {t('• JOYAJOY Creator Subscription (1 month)')}
                </li>
                <li>{t('• Free WEA courses')}</li>
              </ul>
              <p className="text-black-s">
                <span className="click-here" onClick={onClickHere}>
                  {t('CLICK HERE')}
                </span>
                {t(' to start referring new members and earn WEA Cash!')}
              </p>
            </div>
          </div>
        )}
        <div className="centered-container">
          <div className="language-block">
            <LanguageRow />
            <ChangeCountry />
          </div>
        </div>
        <div className="centered-container-no-pad">
          <div className="title-background">
            <img src={getWEAProgramImage('img1')} className="img1" />
            <div className="WEAIcon">
              <img src={getWEAProgramImage('img2')} className="img2" />
            </div>
          </div>

          <p className="text-weablue1-l">{t('WEA Teacher Membership')}</p>
          <p className="text-black-m">
            {t('Transform your teaching journey!')}
          </p>
          <p className="text-black-s">
            {t(
              'Gain access to cutting-edge EdTech tools. Unlock free WEA courses to enhance your teaching. Earn WEA Cash by referring new members!',
            )}
          </p>
        </div>

        <div className="centered-container">
          <p className="text-weablue2-l">{t('WEA Teacher Membership')}</p>
          <div className="container-grey-round">
            <p className="text-black-m">{t('Original Price:')}</p>
            <p className="text-grey-l-crossed">
              {t('{YEAR} / year', {
                YEAR: originalYearCurrency,
              })}
            </p>
            <p className="text-grey-s">
              {t('(About {MONTH} per month)', {
                MONTH: originalMonthCurrency,
              })}
            </p>
            {referralYearCurrency && referralMonthCurrency ? (
              <>
                <hr />
                <p className="text-black-m">
                  {isReferred ? t('Referral Price:') : t('Special Price:')}
                </p>
                <p className="text-weablue1-l">
                  {t('{YEAR} / year', {
                    YEAR: referralYearCurrency,
                  })}
                </p>
                <p className="text-grey-s">
                  {t('(About {MONTH} per month)', {
                    MONTH: referralMonthCurrency,
                  })}
                </p>
              </>
            ) : null}
          </div>
          <TextButton
            text={t(purchasedPlan ? 'Purchased' : 'Purchase Now')}
            size={TextButtonSize.Large}
            fontSize={TextButtonFontSize.Medium}
            variant={
              purchasedPlan
                ? TextButtonVariant.Purchased
                : TextButtonVariant.WEABlue
            }
            disabled={purchasedPlan}
            onClick={onClickPurchase}
          />
          <p className="text-grey-s">
            {t('Payment and membership system powered by JOYAJOY.')}
          </p>
        </div>

        <div className="centered-container-yellow">
          <p className="text-weablue1-l">{t('Member Benefit 1:')}</p>
          <p className="text-weablue2-l">
            {t('JOYAJOY Creator Subscription 1 Month ({price} worth)', {
              price: `${currency}${numbro(
                JOYAJOYPrice[currency].JOYAJOYCreatorMonthly,
              ).format({ thousandSeparated: true })}`,
            })}
          </p>
          <p className="text-black-s">
            {t(
              'Create up to 4,500 AI Memory games monthly on JOYAJOY, the world’s most powerful learning tool. Convert your education content into AI Memory Games, have fun with your students!',
            )}
          </p>
          <div className="smart-video">
            <VideoPlayer
              url={getCreateGameVideo(i18n.language)}
              thumbnailUrl={getTextToGameVideoPreview(i18n.language)}
              playIcon={<></>}
              playing={true}
            />
          </div>
        </div>

        <div className="centered-container-nobg">
          <div className="centered-container-blue">
            <p className="text-weablue1-l">{t('Member Benefit 2:')}</p>
            <p className="text-weablue2-l">
              {t('Professional Development with WEA Courses')}
            </p>
            <p className="text-black-s">
              {t(
                'Enjoy free courses by WEA covering different educational topics, subjects and languages. Complete courses to get international WEA certificates!',
              )}
            </p>
            <div className="logo-container">
              <img src={getWEAProgramImage('img3')} className="logo-icon" />
            </div>
          </div>
        </div>
        <div className="centered-container-nobg">
          <div className="container-purple-light">
            <p className="text-weablue1-l">{t('Member Benefit 3:')}</p>
            <p className="text-weablue2-l">
              {t('Earn WEA Cash by Referring New Users')}
            </p>
            <p className="text-black-s">
              {t(
                "For each member you successfully refer, you'll receive {price} WEA Cash. Spend your WEA Cash in futures purchase of WEA courses as cash.",
                {
                  price: `${currency}${numbro(
                    WEATeacherPlanPrice[currency].tier1PurchaseReward,
                  ).format({ thousandSeparated: true })}`,
                },
              )}
            </p>
            <div className="logo-container">
              <img src={getWEAProgramImage('img4')} className="img4" />
            </div>
            <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.WEABlue}
              onClick={onClickReferral}
            />
          </div>
        </div>

        <div className="centered-container">
          <p className="text-weablue2-l">{t('WEA Teacher Membership')}</p>
          <div className="container-grey-round">
            <p className="text-black-m">{t('Original Price:')}</p>
            <p className="text-grey-l-crossed">
              {t('{YEAR} / year', {
                YEAR: originalYearCurrency,
              })}
            </p>
            <p className="text-grey-s">
              {t('(About {MONTH} per month)', {
                MONTH: originalMonthCurrency,
              })}
            </p>
            {referralYearCurrency && referralMonthCurrency ? (
              <>
                <hr />
                <p className="text-black-m">
                  {isReferred ? t('Referral Price:') : t('Special Price:')}
                </p>
                <p className="text-weablue1-l">
                  {t('{YEAR} / year', {
                    YEAR: referralYearCurrency,
                  })}
                </p>
                <p className="text-grey-s">
                  {t('(About {MONTH} per month)', {
                    MONTH: referralMonthCurrency,
                  })}
                </p>
              </>
            ) : null}
          </div>
          <TextButton
            text={t(purchasedPlan ? 'Purchased' : 'Purchase Now')}
            size={TextButtonSize.Large}
            fontSize={TextButtonFontSize.Medium}
            variant={
              purchasedPlan
                ? TextButtonVariant.Purchased
                : TextButtonVariant.WEABlue
            }
            disabled={purchasedPlan}
            onClick={onClickPurchase}
          />
          <p className="text-grey-s">
            {t('Payment and membership system powered by JOYAJOY.')}
          </p>
        </div>

        <div className="centered-container-nobg">
          <div className="container-weablue1">
            <p className="text-white-l">{t('About WEA')}</p>
            <p className="text-white-s">
              {t(
                'World Educators Association (WEA) has been incorporated in the United States for over 20 years and has organized international educational programs and events in over 100 countries.',
              )}
            </p>
            <img src={getWEAProgramImage('img5')} className="img13" />
            <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.WEABlueText}
              onClick={onClickWEA}
            />
          </div>
        </div>

        <div className="centered-container">
          <p className="text-weablue1-l">
            {t('The Global Contribution Award for Sustainability (ESG)')}
          </p>
          <p className="text-weablue2-l">{t('HKCT Business Awards 2023')}</p>
          <div className="logo-container">
            <img src={getWEAProgramImage('img6')} className="logo-icon" />
          </div>
          <p className="text-black-s">
            {t(
              'The Honorary Chairman of HKIM, Dr. Michael Chan, presented The Global Contribution Award for Sustainability (ESG) to WEA.',
            )}
          </p>
        </div>
        <div className="centered-container">
          <img src={getWEAProgramImage('img7')} className="img7" />
          <p className="text-weablue1-l">{t('WEA’s Technology Partner')}</p>
          <p className="text-black-s">
            {t(
              'JOYAJOY is the world’s most powerful learning app, providing an engaging and empowering space for Learners, Educators and Professionals to interact, learn and share joy.',
            )}
          </p>
          <img src={getWEAProgramImage('img8')} className="img7" />
          <div className="container-icon-with-text2">
            <TextButton
              text={t('Learn More')}
              size={TextButtonSize.Large}
              fontSize={TextButtonFontSize.Medium}
              variant={TextButtonVariant.WEABlue}
              onClick={onClickJoyajoy}
            />
          </div>
        </div>
        {validationLoading && <LoadingSpinner loading={validationLoading} />}
      </div>
    );
  },
);
